/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { useCallback, useState } from 'react';

type SetStateAction<T> = T | ((prev: T) => T);

type UseSemiControlledStateProps<T> = {
  state: T;
  onChange?: (next: T) => void;
  isControlled?: boolean;
};

export function useSemicontrolledState<T>({
  state,
  onChange,
  isControlled,
}: UseSemiControlledStateProps<T>): [
  T,
  (setState: SetStateAction<T>) => void,
  (state: T) => void,
] {
  const [internal, setInternal] = useState<T>(state);

  const current = isControlled === true ? state : internal;

  const setState = useCallback(
    (next: SetStateAction<T>) => {
      const resolved =
        typeof next === 'function' ? (next as (prev: T) => T)(current) : next;

      if (isControlled !== true) {
        setInternal(resolved);
      }
      onChange?.(resolved);
    },
    [isControlled, current, onChange],
  );

  const resetInternalState = useCallback(
    (nextState: T) => {
      if (isControlled !== true) {
        setInternal(nextState);
      }
    },
    [isControlled],
  );

  return [current, setState, resetInternalState];
}
